// EDEN · the planning workspace — the plan and the lesson's materials side by side (teacher review, 15 Oct 2026).
//
// What the teachers reported, and what this answers:
//   1. "When they click Plan this lesson they can't type the plan and look at the materials" — the materials lived on
//      the lesson page (/map/…) and the plan on /plan/new, so every look meant leaving the plan and coming back.
//   6. "Continue plan opens Edit your plan: many boxes and nothing to look at" — the edit screen had no materials at all.
//   4. Plans start from My plans, Home or the Year plan — every road now ends on this SAME screen.
//
// One frame for /plan/new and /lesson/:id/edit: the teacher's writing on one side, the lesson's materials on the other
// (Lesson · Book · Worksheet · Teacher Pack), each opening INSIDE the panel — nothing navigates away from the plan.
// On a computer both sit side by side; on a phone (or a narrow window) a two-way switch at the top flips between
// "Write the plan" and "Lesson materials", and both stay in the page, so nothing typed is ever lost.
//
// The panel only shows materials; it never fills a plan box (Module D rails: the teacher types the thinking cells and
// the five steps in their own words). Its two writing actions go to places that accept them: "Add these books to
// Materials" (the pasteable Materials box) and "Add these pages to my plan" (the plan's "Book pages added to this plan"
// list, apart from the teacher's own activities, links and files).

import "./styles/workspace.css";
import { esc } from "./dom";
import { icon } from "./icons";
import { loadLessonView } from "./pages/lesson-map";
import { glanceHtml, guideLinkLabel, whatToTeachHtml } from "./pages/lesson-map";
import { materialGuideCatalogue, type MaterialGuideEntry } from "./materials-engine";
import { glanceFor } from "./library";
import { booksCardHtml, booksPlainLines, coursebookTarget, refsFor, teacherTitle, workbookTarget, type LessonRef } from "./lesson-refs";
import { expandPages, pageUrl, registerTarget, type ReaderTarget } from "./book-reader";
import { mountPdfInline, openDocument, readySheet } from "./doc-viewer";
import { nodeWhen } from "./lesson-data";

export type PanelTab = "lesson" | "book" | "sheet" | "pack";

export interface WorkspaceOptions {
  /** Explicit source append; never writes teacher-thinking or procedure fields. */
  onUseSyllabus?: (lines: string[]) => void;
  lessonId?: () => string | null;
  termId?: () => string | null;
  weekNo?: () => number | null;
  prepareWorksheet?: () => Promise<void>;
  /** Called with the plain book lines when the teacher presses "Add these books to Materials". */
  onAddBooks?: (lines: string[]) => void;
  /** Adds the week's Student Book pages to the plan's book-pages list (src/plan-resources-ui.ts). Absent → no button. */
  onAddPages?: (page: { book_id: string; pages: string; title: string }) => Promise<void>;
  /** The class the plan is for — worksheets open in the maker for this class. */
  classId?: () => string | null;
}

export interface WorkspaceHandle {
  /** Show the materials of another week (the plan form's week or lesson picker changed). Null = a plan with no week. */
  setNode(nodeId: string | null): void;
  /** The panel's heading, in the page's words: "Week 5 · 1 Dec – 7 Dec · <lesson title>", the SAME week the Week picker
   *  shows (owner ruling A3, 21 Oct 2026). Null = the default, "Lesson materials". */
  setHeading(text: string | null): void;
  /** Flip the phone view to the materials (optionally a given tab). */
  showMaterials(tab?: PanelTab): void;
}

/** The frame. `form` is the plan form's HTML; the panel is filled by mountWorkspace(). */
export function workspaceHtml(form: string): string {
  return `<div class="pw" data-pw data-view="form">
    <div class="pw-switch" role="group" aria-label="Plan or materials">
      <button type="button" class="pw-sw" data-pw-view="form" aria-pressed="true" aria-controls="pw-form">${icon("pencil", "ico-sm")}<span>Write the plan</span></button>
      <button type="button" class="pw-sw" data-pw-view="ref" aria-pressed="false" aria-controls="pw-ref">${icon("book", "ico-sm")}<span>Lesson materials</span></button>
    </div>
    <div class="pw-form" id="pw-form" data-pw-form>${form}</div>
    <aside class="pw-ref" id="pw-ref" data-pw-ref aria-label="Lesson materials">
      <div class="pw-ref-head">
        <p class="pw-ref-title">${icon("book", "ico-sm")}<span data-pw-title>Lesson materials</span></p>
        <p class="pw-ref-sub muted small" data-pw-sub>Nothing you type is lost while you look here.</p>
        <div class="pw-tabs" role="tablist" aria-label="Materials" data-pw-tabs></div>
      </div>
      <div class="pw-ref-body" data-pw-body><p class="muted small">Loading this week's materials…</p></div>
    </aside>
  </div>`;
}

interface PanelData {
  nodeId: string;
  view: Awaited<ReturnType<typeof loadLessonView>>;
  ref: LessonRef | null;
  glance: Awaited<ReturnType<typeof glanceFor>>;
  /** which readable guide the week has (complete / activities only / …), for the guide fold's words */
  guide: MaterialGuideEntry | null;
}

const TAB_LABEL: Record<PanelTab, string> = { lesson: "How to teach", book: "Book", sheet: "Worksheet", pack: "Teacher Pack" };
const TAB_ICON: Record<PanelTab, Parameters<typeof icon>[0]> = { lesson: "check", book: "book", sheet: "sheet", pack: "pack" };
const LG_TEACHING = /^(?:LG[1-6]|LGB[12])-SB$/;
const TAB_KEY = "eden.pw.tab";

export function mountWorkspace(root: HTMLElement, nodeId: string | null, opts: WorkspaceOptions = {}): WorkspaceHandle {
  const pw = root.querySelector<HTMLElement>("[data-pw]")!;
  const tabsEl = pw.querySelector<HTMLElement>("[data-pw-tabs]")!;
  const body = pw.querySelector<HTMLElement>("[data-pw-body]")!;
  const sub = pw.querySelector<HTMLElement>("[data-pw-sub]")!;
  const titleEl = pw.querySelector<HTMLElement>("[data-pw-title]")!;
  let heading: string | null = null;
  let data: PanelData | null = null;
  let current: string | null = null;
  let seq = 0;
  let tab: PanelTab = ((): PanelTab => {
    try { const v = localStorage.getItem(TAB_KEY); return v === "book" || v === "sheet" || v === "pack" || v === "lesson" ? v : "lesson"; }
    catch { return "lesson"; }
  })();
  let inline: { destroy(): void } | null = null;
  const scrollAt: Record<"form" | "ref", number> = { form: 0, ref: 0 };

  // ── the phone switch: both panes stay in the page; only one shows at a time below the two-column width. Two
  //    plain toggle buttons (aria-pressed): each is its own Tab stop and Enter/Space press it — no tab-list rules. ──
  function setView(v: "form" | "ref"): void {
    const was = pw.dataset.view as "form" | "ref";
    if (was === v) return;
    scrollAt[was] = window.scrollY;
    pw.dataset.view = v;
    pw.querySelectorAll<HTMLButtonElement>("[data-pw-view]").forEach((b) => b.setAttribute("aria-pressed", String(b.dataset.pwView === v)));
    // Come back to where the teacher was in that pane (the top of the switch when it is the first visit).
    const top = pw.getBoundingClientRect().top + window.scrollY - 8;
    window.scrollTo({ top: scrollAt[v] || Math.max(0, top) });
  }
  pw.querySelectorAll<HTMLButtonElement>("[data-pw-view]").forEach((b) => b.addEventListener("click", () => setView(b.dataset.pwView as "form" | "ref")));

  // ── tabs ──
  function availableTabs(): PanelTab[] {
    if (!data?.view) return ["lesson"];
    const out: PanelTab[] = ["lesson"];
    if (coursebookTarget(data.ref) || data.ref) out.push("book");
    if (data.view.mat?.sheet) out.push("sheet");
    if (data.view.mat?.pack) out.push("pack");
    return out;
  }
  body.id ||= `pw-body-${Math.random().toString(36).slice(2, 8)}`;
  body.setAttribute("role", "tabpanel");
  function paintTabs(): void {
    const tabs = availableTabs();
    if (!tabs.includes(tab)) tab = "lesson";
    // one Tab stop for the row (the chosen tab); the arrow keys move between tabs (below)
    tabsEl.innerHTML = tabs.map((t) => `<button type="button" role="tab" class="pw-tab" data-pw-tab="${t}" aria-selected="${t === tab}" aria-controls="${body.id}" tabindex="${t === tab ? 0 : -1}">${icon(TAB_ICON[t], "ico-sm")}<span>${TAB_LABEL[t]}</span></button>`).join("");
    tabsEl.hidden = tabs.length < 2;
  }
  function choose(t: PanelTab, focus = false): void {
    tab = t;
    try { localStorage.setItem(TAB_KEY, tab); } catch { /* private mode */ }
    paintTabs();
    paintBody();
    if (focus) tabsEl.querySelector<HTMLElement>(`[data-pw-tab="${t}"]`)?.focus();
  }
  tabsEl.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-pw-tab]");
    if (b) choose(b.dataset.pwTab as PanelTab);
  });
  tabsEl.addEventListener("keydown", (e) => {
    const tabs = availableTabs();
    const at = tabs.indexOf(tab);
    const to = e.key === "ArrowRight" ? (at + 1) % tabs.length : e.key === "ArrowLeft" ? (at - 1 + tabs.length) % tabs.length
      : e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    choose(tabs[to]!, true);
  });

  async function load(id: string | null): Promise<void> {
    current = id;
    const mine = ++seq;
    inline?.destroy();
    inline = null;
    const source = root.querySelector<HTMLElement>("[data-pw-source]");
    if (source) source.innerHTML = id ? `<p>Loading this week's syllabus…</p>` : `<h2 class="card-title">From this week's syllabus</h2><p>No source objectives are selected. Write your own in Objectives / Materials.</p>`;
    if (!id) {
      data = null;
      paintTabs();
      sub.textContent = "This plan is not linked to a week of the year plan.";
      body.innerHTML = `<div class="pw-empty"><p>Pick the week of the year plan this lesson is for, and its book pages, worksheet and teaching steps appear here.</p></div>`;
      return;
    }
    body.innerHTML = `<p class="muted small">Loading this week's materials…</p>`;
    let loaded: [PanelData["view"], PanelData["ref"], PanelData["glance"], PanelData["guide"]];
    try {
      loaded = await Promise.all([loadLessonView(id), refsFor(id), glanceFor(id),
        materialGuideCatalogue().then((list) => list.find((d) => d.node_id === id) ?? null).catch(() => null)]);
    } catch {
      if (mine !== seq) return;
      const failure = `<p>The syllabus and materials could not be loaded.</p><button type="button" class="btn secondary" data-pw-retry>Retry</button>`;
      body.innerHTML = failure;
      if (source) source.innerHTML = failure;
      root.querySelectorAll("[data-pw-retry]").forEach((button) => button.addEventListener("click", () => void load(id)));
      return;
    }
    const [view, ref, glance, guide] = loaded;
    if (mine !== seq) return; // the step changed again while this loaded
    data = { nodeId: id, view, ref, glance, guide };
    if (!view && source) source.innerHTML = `<p>No source objectives are recorded for this selection. Write your own in Objectives / Materials.</p>`;
    // With the page's own heading the week it names is the picker's; the lesson's own week is the fallback.
    sub.textContent = view && heading === null ? `${nodeWhen(view.node)} · nothing you type is lost while you look here.` : "Nothing you type is lost while you look here.";
    paintTabs();
    paintBody();
  }

  function paintBody(): void {
    inline?.destroy();
    inline = null;
    stopTrack(); // its button is about to be replaced
    if (!data?.view) {
      if (current) body.innerHTML = `<div class="pw-empty"><p>This week is not in the year plan any more.</p></div>`;
      return;
    }
    const { view, ref, glance } = data;
    const source = root.querySelector<HTMLElement>("[data-pw-source]");
    if (source) {
      const aims = view.node.objectives ?? [];
      const grammar = view.node.coursebook?.grammar;
      const books = booksPlainLines(ref);
      const lines = [
        `From this week's syllabus (${view.node.id})`,
        ...aims.map((a) => `Source aim: ${a}`),
        ...(grammar ? [`Grammar: ${grammar}`] : []),
        ...books,
      ];
      source.innerHTML = `<h2 class="card-title">From this week's syllabus</h2>
        <p class="hint">These are the aims recorded for this week. They may cover only part of the coursebook lesson. Add your own class decisions below.</p>
        ${aims.length ? `<ul>${aims.map((a) => `<li>${esc(a)}</li>`).join("")}</ul>` : `<p>No source objectives are recorded for this week. Write your own objectives in Objectives / Materials.</p>`}
        ${grammar ? `<p><b>Grammar:</b> ${esc(grammar)}</p>` : ""}
        ${books.length ? `<ul>${books.map((b) => `<li>${esc(b)}</li>`).join("")}</ul>` : ""}
        <button type="button" class="btn secondary" data-use-syllabus>Use these starting points</button>
        <p class="hint">Adds to Objectives / Materials and keeps your writing. Values or character connections are optional.</p>`;
      source.querySelector("[data-use-syllabus]")?.addEventListener("click", () => opts.onUseSyllabus?.(lines));
    }
    if (tab === "lesson") body.innerHTML = lessonTab(view, glance);
    else if (tab === "book") paintBook(view, ref);
    else if (tab === "sheet") paintSheet(view, ref);
    else paintPack(view);
    body.scrollTop = 0;
  }

  // ── Lesson: how to teach it, in plain words ─────────────────────────────────────────────────
  function lessonTab(view: NonNullable<PanelData["view"]>, glance: PanelData["glance"]): string {
    const mat = view.mat;
    const how = glanceHtml(glance, mat?.pack ?? null, { bare: true });
    const one = mat?.oneliner ? `<p class="pw-one">${esc(plainOne(mat.oneliner))}</p>` : "";
    const facts = whatToTeachHtml(view.cur, view.node, mat, view.reg, true, { slim: !!how });
    // The readable guide, in the words its contents deserve ("Read the classroom activities" for Grades 7–10);
    // a week whose guide is only "awaiting review" or "not found" gets no fold at all.
    const words = guideLinkLabel(data?.guide?.state);
    const guide = words ? `<details class="pw-guide" data-pw-guide><summary>${icon("pack", "ico-sm")}<span>${esc(words)}</span></summary><div class="pw-guide-body"><p class="muted small">Loading…</p></div></details>` : "";
    return `${one}${how || ""}${facts}${guide}`;
  }

  // ── Book: the Student Book pages WITH the Teacher's Guide notes beside each page (what teachers asked for) ──
  function paintBook(view: NonNullable<PanelData["view"]>, ref: LessonRef | null): void {
    const cb = coursebookTarget(ref);
    const wb = workbookTarget(ref);
    const lines = booksPlainLines(ref);
    const tools = `<div class="pw-tools">
      ${lines.length && opts.onAddBooks ? `<button type="button" class="btn secondary sm" data-pw-add-books>${icon("plus", "ico-sm")}<span>Add these books to Materials</span></button>` : ""}
      ${cb && opts.onAddPages ? `<button type="button" class="btn secondary sm" data-pw-add-pages>${icon("plus", "ico-sm")}<span>Add these pages to my plan</span></button>` : ""}
    </div>`;
    const explain = `<p class="pw-explain small">${icon("info", "ico-sm")}<span><b>Student Book</b> = the pages your students see. <b>Teacher's Guide notes</b> = the teaching steps the Teacher's Guide gives for that page, typed out so they are easy to read. <b>Teacher's Guide page</b> = a photo of the original page.</span></p>`;
    const pages = cb ? expandPages(cb.target.pages) : [];
    const delta = (pdf: number): number => (cb!.target.pages.find(([a, b]) => pdf >= a && pdf <= b)?.[2] ?? 0);
    const pageCards = cb ? pages.map((p) => {
      const one: ReaderTarget = { ...cb.target, pages: [[p.pdf, p.pdf, delta(p.pdf)]], label: `${cb.target.label} · page ${p.printed}`, unit: { label: cb.target.label, pages: cb.target.pages } };
      return `<article class="pw-page" data-pw-page="${p.pdf}">
        <button type="button" class="pw-page-pic" data-book-open="${registerTarget(one)}" aria-label="Open page ${p.printed} large">
          <img loading="lazy" alt="${esc(cb.book)}, page ${p.printed}" src="${esc(pageUrl(cb.target.bookId, p.pdf))}" />
          <span class="pw-page-no">Page ${p.printed}</span>
        </button>
        <div class="pw-notes" data-pw-notes="${p.pdf}">${LG_TEACHING.test(cb.target.bookId) ? `<p class="muted small">Loading the Teacher's Guide notes…</p>` : `<p class="muted small">This book has no typed guide notes. Open the page to read it large.</p>`}</div>
      </article>`;
    }).join("") : "";
    const head = cb
      ? `<div class="pw-book-head"><p><b>${esc(cb.book)}</b>${cb.unit ? ` · ${esc(cb.unit)}` : ""}${cb.pagesLabel ? ` · ${esc(cb.pagesLabel)}` : ""}</p>
          <button type="button" class="btn sm" data-book-open="${registerTarget(cb.target)}">${icon("book", "ico-sm")}<span>Open the pages large</span></button></div>`
      : `<p class="muted small">This week has no Student Book pages online. The books for the week are listed below.</p>`;
    const wbRow = wb ? `<p class="pw-wb">${icon("pencil", "ico-sm")}<span><b>Workbook</b> · ${esc(wb.pagesLabel)}</span>
        <button type="button" class="btn secondary sm" data-book-open="${registerTarget(wb.target)}">${icon("book", "ico-sm")}<span>Open</span></button></p>` : "";
    body.innerHTML = `${explain}${head}${tools}<div class="pw-pages">${pageCards}</div>${wbRow}
      <details class="pw-allbooks"><summary>${icon("book", "ico-sm")}<span>All the books for this week</span></summary>${booksCardHtml(ref, { compact: true, quiet: true, bare: true })}</details>`;
    body.querySelector<HTMLButtonElement>("[data-pw-add-books]")?.addEventListener("click", () => opts.onAddBooks?.(lines));
    body.querySelector<HTMLButtonElement>("[data-pw-add-pages]")?.addEventListener("click", async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      if (!cb) return;
      b.disabled = true;
      try {
        await opts.onAddPages?.({ book_id: cb.target.bookId, pages: cb.pagesLabel.replace(/^pages? /, ""), title: `${cb.book}${cb.unit ? ` · ${cb.unit}` : ""}${cb.pagesLabel ? ` · ${cb.pagesLabel}` : ""}` });
      } finally { b.disabled = false; }
    });
    if (cb && LG_TEACHING.test(cb.target.bookId)) void loadNotes(cb.target.bookId, pages.map((p) => p.pdf), seq);
    void view;
  }

  async function loadNotes(bookId: string, pdfs: number[], mine: number): Promise<void> {
    for (const pdf of pdfs) {
      const box = body.querySelector<HTMLElement>(`[data-pw-notes="${pdf}"]`);
      if (!box) return;
      let entry: GuidePage | null = null;
      let failed: "session" | "other" | null = null;
      try {
        const r = await fetch(`/m/${encodeURIComponent(bookId)}/guide/${pdf}`, { credentials: "same-origin", cache: "no-store" });
        if (r.status === 401) failed = "session";
        else if (r.ok) entry = (await r.json()) as GuidePage;
        else if (r.status !== 404) failed = "other";
      } catch { failed = "other"; }
      if (mine !== seq || !body.contains(box)) return; // body.contains, not isConnected: the page may still be off-screen (router)
      if (failed === "session") { box.innerHTML = `<p class="small">Your sign-in has ended, so the guide notes are hidden. <a href="/signin">Sign in again</a></p>`; continue; }
      if (failed) { box.innerHTML = `<p class="small muted">The guide notes did not load. Check the connection.</p>`; continue; }
      if (!entry || (!entry.steps.length && !entry.note)) { box.innerHTML = `<p class="muted small">No guide notes for this page.</p>`; continue; }
      const guideBtn = entry.guide?.pdf?.length
        ? `<button type="button" class="linkbtn small" data-book-open="${registerTarget({ book: entry.guide.book, bookId: entry.guide.book_id, label: entry.guide.label, pages: entry.guide.pdf, teacherOnly: true })}">${icon("pack", "ico-sm")} See the Teacher's Guide page (photo)</button>` : "";
      const tracks = entry.tracks.filter((t) => t.available).map((t) => `<button type="button" class="pw-track" data-pw-track="${esc(t.id)}" data-pw-book="${esc(bookId)}">▶ Track ${esc(t.id)}</button>`).join("");
      box.innerHTML = `<p class="pw-notes-h">${icon("check", "ico-sm")}<span>Teacher's Guide notes${entry.title ? ` · ${esc(entry.title)}` : ""}</span></p>
        ${entry.note ? `<p class="small muted">${esc(entry.note)}</p>` : ""}
        <ol class="pw-steps">${entry.steps.map((s) => `<li>${esc(s)}</li>`).join("")}</ol>
        ${tracks ? `<div class="pw-tracks" aria-label="Class audio">${tracks}</div>` : ""}
        ${guideBtn}`;
    }
  }

  // one small player for the guide notes' tracks — stopped when the panel redraws or the teacher leaves the page
  let audio: HTMLAudioElement | null = null;
  let playing = "";
  let leaving = 0;
  function stopTrack(): void {
    if (audio && !audio.paused) audio.pause();
    window.clearInterval(leaving);
    leaving = 0;
  }
  body.addEventListener("click", (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>("[data-pw-track]");
    if (!t) return;
    const src = `/m/${encodeURIComponent(t.dataset.pwBook ?? "")}/audio/${encodeURIComponent(t.dataset.pwTrack ?? "")}`;
    audio ??= new Audio();
    body.querySelectorAll<HTMLElement>("[data-pw-track]").forEach((b) => (b.textContent = `▶ Track ${b.dataset.pwTrack}`));
    if (playing === src && !audio.paused) { audio.pause(); return; }
    if (playing !== src) { audio.src = src; playing = src; }
    // the router swaps pages without a teardown: a page that is gone stops its sound
    window.clearInterval(leaving);
    leaving = window.setInterval(() => { if (!pw.isConnected) stopTrack(); }, 1000);
    void audio.play().then(() => { t.textContent = `❚❚ Track ${t.dataset.pwTrack}`; }).catch(() => { t.textContent = `Track ${t.dataset.pwTrack} did not play`; });
  });

  // ── the full teacher guide, loaded only when its fold opens ──
  body.addEventListener("toggle", (e) => {
    const d = e.target as HTMLElement;
    if (!(d instanceof HTMLDetailsElement) || !d.open || !d.matches("[data-pw-guide]") || d.dataset.loaded || !data) return;
    d.dataset.loaded = "1";
    void loadGuide(d.querySelector<HTMLElement>(".pw-guide-body")!, data.nodeId);
  }, true);

  async function loadGuide(box: HTMLElement, id: string): Promise<void> {
    try {
      const [{ loadMaterialGuide, guideHtml }, guidePage] = await Promise.all([import("./materials-engine"), import("./pages/materials-guide"), import("./styles/materials-engine.css")]);
      const res = await loadMaterialGuide(id);
      if (!body.contains(box)) return;
      if (!res.document) { box.innerHTML = `<p class="muted small">There is no readable guide for this week yet. The Teacher Pack tab has the full pack.</p>`; return; }
      const { html } = guidePage.readableGuideSections(res.document.guide, id);
      const overview = res.document.overview;
      box.innerHTML = `<div class="me-shell me-guide pw-guide-doc">${overview ? `<p><b>Learning goal:</b> ${esc(overview.goal)}</p><p class="me-warning"><b>Watch for:</b> ${esc(overview.watch_for)}</p>` : ""}${html || guideHtml(res.document.guide)}</div>`;
    } catch (error) {
      if (body.contains(box)) box.innerHTML = `<p class="small muted">The full guide did not load (${esc((error as Error).message)}). The Teacher Pack tab has the full pack.</p>`;
    }
  }

  // ── Worksheet: this week's sheet, cleaned for teachers, inside the panel ──
  function paintSheet(view: NonNullable<PanelData["view"]>, ref: LessonRef | null): void {
    const lessonName = teacherTitle(view.node, ref); // the name the lesson page shows, never a code
    const mat = view.mat!;
    const cls = opts.classId?.() ?? null;
    const context = new URLSearchParams();
    if (cls) context.set("class", cls);
    const lesson = opts.lessonId?.();
    const term = opts.termId?.();
    const week = opts.weekNo?.();
    if (lesson) context.set("lesson", lesson);
    if (term) context.set("term", term);
    if (week) context.set("week", String(week));
    const maker = `/materials-studio/${encodeURIComponent(view.node.id)}?${context}`;
    body.innerHTML = `<div class="pw-tools">
        <a class="btn sm" href="${esc(maker)}" data-pw-make>${icon("print", "ico-sm")}<span>Make a worksheet for this week</span></a>
        <button type="button" class="btn secondary sm" data-pw-sheet-full>${icon("eye", "ico-sm")}<span>Full screen</span></button>
      </div>
      <p class="muted small">Your plan saves before the worksheet maker opens. Choose activities, preview and print there.</p>
      <p class="error" data-pw-make-error hidden></p>
      <iframe class="pw-sheet" title="This week's worksheet" src="${esc(mat.sheet!)}" loading="lazy"></iframe>`;
    body.querySelector<HTMLAnchorElement>("[data-pw-make]")?.addEventListener("click", (event) => {
      event.preventDefault();
      const error = body.querySelector<HTMLElement>("[data-pw-make-error]")!;
      void (async () => {
        try {
          await opts.prepareWorksheet?.();
          // Read every context field after persistence, including a newly created real lesson.
          const fresh = new URLSearchParams();
          const fields = { class: opts.classId?.(), lesson: opts.lessonId?.(), term: opts.termId?.(), week: opts.weekNo?.() };
          for (const [key, value] of Object.entries(fields)) if (value != null && value !== "") fresh.set(key, String(value));
          const { navigate } = await import("./router");
          navigate(`/materials-studio/${encodeURIComponent(view.node.id)}?${fresh}`);
        } catch (ex) {
          error.textContent = `Your plan could not be saved. ${(ex as Error).message} Retry here before opening the worksheet.`;
          error.hidden = false;
        }
      })();
    });
    const frame = body.querySelector<HTMLIFrameElement>("iframe.pw-sheet")!;
    frame.style.visibility = "hidden"; // shown once its notes are in teacher words (readySheet), never with the codes first
    frame.addEventListener("load", () => void (async () => {
      try {
        const doc = frame.contentDocument;
        if (doc?.querySelector(".sheet")) await readySheet(doc, lessonName);
        // Fit the frame to its sheet, so the panel scrolls once (no frame-in-a-frame scrolling on a phone), and fit it
        // again whenever the sheet's height changes (its bar's "Show the long notes" and "Print student copy", the
        // "for checking" fold, a phone turned). No sheet sizes its page by its window (none of the 217 uses "vh" or
        // "height: 100%"), so the html element's height is the sheet's own, whatever height the frame has. The cap
        // only stops a page that did from growing the frame without end: the tallest sheet today, Grade 6 week 11 with
        // the long notes and the fold open, is about 30,000 px on a 390 px wide phone and 33,500 px on a 360 px one.
        const sheet = doc?.documentElement;
        const fit = (): void => {
          const h = sheet ? Math.ceil(sheet.getBoundingClientRect().height) : 0;
          if (h) frame.style.height = `${Math.min(h + 24, 100000)}px`;
        };
        fit();
        const Observer = (frame.contentWindow as (Window & typeof globalThis) | null)?.ResizeObserver;
        if (sheet && Observer) new Observer(fit).observe(sheet);
      } catch { /* a cross-origin or failed sheet keeps its own look */ }
      frame.style.visibility = "";
    })());
    body.querySelector<HTMLButtonElement>("[data-pw-sheet-full]")?.addEventListener("click", (e) =>
      openDocument({ url: mat.sheet!, title: lessonName, kind: "sheet", opener: e.currentTarget as HTMLElement }));
  }

  // ── Teacher Pack: the PDF, drawn inside the panel (never an outside PDF program) ──
  function paintPack(view: NonNullable<PanelData["view"]>): void {
    const mat = view.mat!;
    body.innerHTML = `<p class="muted small pw-pack-note">The full Teacher Pack. The "How to teach" tab has the same lesson in fewer words.</p><div class="pw-pdf" data-pw-pdf></div>`;
    inline = mountPdfInline(body.querySelector<HTMLElement>("[data-pw-pdf]")!, mat.pack!, { title: `Teacher Pack · ${nodeWhen(view.node)}` });
  }

  void load(nodeId);
  return {
    setNode(id: string | null): void {
      if (id === current) return;
      void load(id);
    },
    setHeading(text: string | null): void {
      heading = text;
      titleEl.textContent = text ?? "Lesson materials";
      if (text !== null && data?.view) sub.textContent = "Nothing you type is lost while you look here.";
    },
    showMaterials(t?: PanelTab): void {
      if (t) { tab = t; paintTabs(); paintBody(); }
      setView("ref");
    },
  };
}

interface GuidePage {
  printed: number | null;
  title: string;
  guide?: { book_id: string; book: string; label: string; pdf: [number, number, number][] };
  steps: string[];
  tracks: { id: string; available: boolean }[];
  note?: string;
}

function plainOne(s: string): string {
  return s.replace(/\s*\(PDF pp?\.\s*\d+(?:\s*[-–]\s*\d+)?\)/g, "").replace(/\bSB (pp?\.)/g, "Student Book $1");
}

